import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileSpreadsheet,
  Filter,
  Loader2,
  LogOut,
  Mail,
  RefreshCw,
  Send,
  Trash2,
  UserPlus,
  Users,
  X,
  UploadCloud,
} from "lucide-react";
import { Invitations, tokenStorage } from "@/lib/api";

const STATUS_STYLE = {
  sent: "bg-blue-500/15 text-blue-200 border-blue-500/30",
  registered: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  expired: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  revoked: "bg-rose-500/15 text-rose-200 border-rose-500/30",
};

const ROLES = ["dsa", "advisor", "associate", "agency", "branch"];

export default function AdminInvitations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await Invitations.list(statusFilter ? { status: statusFilter } : {});
      setItems(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        tokenStorage.clear();
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const doResend = async (inv) => {
    const msg = window.prompt(
      `Resend invitation to ${inv.email}?\n\nOptional note to add to the email (or leave blank):`,
      ""
    );
    if (msg === null) return;
    try {
      const r = await Invitations.resend(inv.id, msg);
      setNotice({
        ok: r.dispatch?.sent,
        text: r.dispatch?.sent
          ? `Resent to ${inv.email} (provider: ${r.dispatch.provider})`
          : `Re-dispatch no-op: ${r.dispatch?.reason || "check EMAIL_PROVIDER"}`,
      });
      await load();
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.detail || e.message });
    }
  };

  const doRevoke = async (inv) => {
    if (!window.confirm(`Revoke the invitation for ${inv.email}? They will no longer be able to register with the sent link.`)) return;
    try {
      await Invitations.revoke(inv.id);
      await load();
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.detail || e.message });
    }
  };

  const counts = items.reduce(
    (acc, it) => ({ ...acc, [it.status]: (acc[it.status] || 0) + 1 }),
    {}
  );

  return (
    <div data-testid="admin-invitations" className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/95 backdrop-blur border-b border-[#D4AF37]/25">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/leads" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#F0D788]">
              <ArrowLeft size={12} /> Back to Admin
            </Link>
            <div className="w-px h-6 bg-white/10 mx-2" />
            <h1 className="font-display text-base md:text-lg flex items-center gap-2">
              <UserPlus size={16} className="text-[#F0D788]" /> Bulk Partner Invitations
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a href="/admin/change-password" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#F0D788] text-[11px] font-semibold">
              Change password
            </a>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-rose-400/40 text-xs text-white/80"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricCard label="Total invites" value={items.length} testId="mi-total" />
          <MetricCard label="Sent" value={counts.sent || 0} accent="text-blue-300" testId="mi-sent" />
          <MetricCard label="Registered" value={counts.registered || 0} accent="text-emerald-300" testId="mi-reg" />
          <MetricCard label="Revoked / Expired" value={(counts.revoked || 0) + (counts.expired || 0)} accent="text-rose-300" testId="mi-rev" />
        </section>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button
            data-testid="new-invite-btn"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-gold font-semibold text-sm"
          >
            <Send size={14} /> New Invitations
          </button>
          <div className="inline-flex items-center gap-2 text-xs text-white/50 ml-1">
            <Filter size={12} />
            <select
              data-testid="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#0A1F44] border border-white/10 rounded-md px-2 py-1"
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="registered">Registered</option>
              <option value="revoked">Revoked</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="refresh-invites"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-white/70"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {notice && (
          <div
            data-testid="invite-notice"
            className={`mb-6 rounded-xl border px-4 py-3 text-xs flex items-center gap-3 ${
              notice.ok
                ? "bg-emerald-400/10 border-emerald-400/35 text-emerald-100"
                : "bg-rose-400/10 border-rose-400/35 text-rose-100"
            }`}
          >
            {notice.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {notice.text}
            <button onClick={() => setNotice(null)} className="ml-auto opacity-70 hover:opacity-100 underline">
              Dismiss
            </button>
          </div>
        )}

        <section className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                <tr>
                  <th className="text-left px-4 py-3">Invitee</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-left px-4 py-3">Expires</th>
                  <th className="text-left px-4 py-3">Resends</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-white/55">
                      <Loader2 className="inline animate-spin mr-2" size={13} /> Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-white/55">
                      No invitations yet. Click <strong>New Invitations</strong> above to send some.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((inv, i) => (
                    <tr key={inv.id} data-testid={`inv-row-${i}`} className="border-t border-white/5">
                      <td className="px-4 py-3">
                        <div className="text-white">{inv.name}</div>
                        <div className="text-[11px] text-white/50">{inv.email}</div>
                        {inv.mobile && <div className="text-[11px] text-white/40">{inv.mobile}</div>}
                      </td>
                      <td className="px-4 py-3 text-white/85 uppercase text-xs tracking-wider">
                        {inv.role}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${STATUS_STYLE[inv.status] || ""}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-white/65">{fmt(inv.sent_at)}</td>
                      <td className="px-4 py-3 text-xs text-white/65">{fmt(inv.expires_at)}</td>
                      <td className="px-4 py-3 text-xs text-white/55 tabular-nums">
                        {inv.resend_count || 0}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {inv.status !== "registered" && inv.status !== "revoked" && (
                          <button
                            onClick={() => doResend(inv)}
                            data-testid={`inv-resend-${i}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#D4AF37]/35 hover:border-[#D4AF37]/70 text-[11px] text-[#F0D788] mr-1.5"
                          >
                            <Mail size={11} /> Resend
                          </button>
                        )}
                        {inv.status !== "revoked" && inv.status !== "registered" && (
                          <button
                            onClick={() => doRevoke(inv)}
                            data-testid={`inv-revoke-${i}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose-400/40 hover:border-rose-400/70 text-[11px] text-rose-300"
                          >
                            <Trash2 size={11} /> Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {showAdd && (
        <NewInvitationsModal
          onClose={() => setShowAdd(false)}
          onSent={(result) => {
            setShowAdd(false);
            setNotice({
              ok: (result.sent_count || 0) > 0,
              text: `Sent: ${result.sent_count} · Duplicates: ${result.duplicate_count} · Invalid: ${result.invalid_count}`,
            });
            load();
          }}
        />
      )}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso).slice(0, 19).replace("T", " ");
  }
}

function MetricCard({ label, value, accent = "text-white", testId }) {
  return (
    <div data-testid={testId} className="bg-[#0A1F44] rounded-xl border border-[#D4AF37]/20 p-4">
      <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

/* ─── New Invitations Modal (paste + CSV) ─────────────────────── */

const rowKey = () => (globalThis.crypto?.randomUUID ? crypto.randomUUID() : `r-${Date.now()}-${Math.random().toString(36).slice(2)}`);
const newRow = () => ({ _k: rowKey(), name: "", email: "", mobile: "", role: "dsa" });

function NewInvitationsModal({ onClose, onSent }) {
  const [mode, setMode] = useState("paste"); // paste | csv
  const [rows, setRows] = useState(() => [newRow()]);
  const [busy, setBusy] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [err, setErr] = useState("");

  const addRow = () => setRows((rs) => [...rs, newRow()]);
  const updateRow = (i, key, val) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const rmRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const cleaned = rows
        .map((r) => ({
          name: (r.name || "").trim(),
          email: (r.email || "").trim().toLowerCase(),
          mobile: (r.mobile || "").trim() || null,
          role: r.role || "dsa",
        }))
        .filter((r) => r.name && r.email && r.email.includes("@"));
      if (cleaned.length === 0) {
        setErr("Please add at least one row with a valid name and email.");
        setBusy(false);
        return;
      }
      const r = await Invitations.bulk(cleaned);
      onSent(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send invitations. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setErr("");
    try {
      const r = await Invitations.uploadCsv(f);
      setCsvPreview(r);
      if (r.rows?.length) setRows(r.rows.map((row) => ({ ...row, _k: rowKey() })));
      setMode("paste"); // switch to review of parsed rows
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not parse CSV. Expected columns: name,email,mobile,role");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="new-invite-modal"
    >
      <div className="w-full max-w-3xl bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/30 my-8 overflow-hidden">
        <div className="p-5 border-b border-white/10 flex items-center gap-2">
          <UserPlus size={16} className="text-[#F0D788]" />
          <h2 className="font-display text-lg">Send New Invitations</h2>
          <button className="ml-auto text-white/50 hover:text-white" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
          <button
            onClick={() => setMode("paste")}
            data-testid="mode-paste"
            className={`px-3 py-1.5 rounded-full text-xs ${mode === "paste" ? "bg-[#D4AF37] text-[#0A1F44] font-semibold" : "border border-white/10 text-white/70"}`}
          >
            <Users size={12} className="inline mr-1" /> Enter manually
          </button>
          <button
            onClick={() => setMode("csv")}
            data-testid="mode-csv"
            className={`px-3 py-1.5 rounded-full text-xs ${mode === "csv" ? "bg-[#D4AF37] text-[#0A1F44] font-semibold" : "border border-white/10 text-white/70"}`}
          >
            <FileSpreadsheet size={12} className="inline mr-1" /> Upload CSV
          </button>
        </div>

        {mode === "csv" && (
          <div className="p-5">
            <label className="border-2 border-dashed border-[#D4AF37]/30 rounded-xl block p-8 text-center cursor-pointer hover:border-[#D4AF37]/60">
              <UploadCloud className="mx-auto mb-2 text-[#F0D788]" size={28} />
              <div className="text-white/85 text-sm">Click to upload a CSV file</div>
              <div className="text-[11px] text-white/50 mt-1">
                Expected columns: <code>name, email, mobile, role</code> (role defaults to <code>dsa</code>)
              </div>
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-input" />
            </label>
            {csvPreview && (
              <div className="mt-3 text-xs text-white/70">
                Parsed <strong>{csvPreview.parsed}</strong> row(s) · {csvPreview.invalid} invalid.
                Switch to <strong>Enter manually</strong> to review before sending.
              </div>
            )}
          </div>
        )}

        {mode === "paste" && (
          <div className="p-5 max-h-[55vh] overflow-y-auto">
            <div className="grid grid-cols-[1fr_1.5fr_1fr_0.8fr_28px] gap-2 text-[10px] uppercase tracking-wider text-white/50 px-1 mb-1">
              <div>Name</div>
              <div>Email</div>
              <div>Mobile</div>
              <div>Role</div>
              <div />
            </div>
            {rows.map((r, i) => (
              <div key={r._k} className="grid grid-cols-[1fr_1.5fr_1fr_0.8fr_28px] gap-2 mb-2">
                <input
                  data-testid={`row-name-${i}`}
                  value={r.name}
                  onChange={(e) => updateRow(i, "name", e.target.value)}
                  placeholder="Full name"
                  className="px-3 py-2 rounded-md bg-white/[0.05] border border-white/10 text-sm outline-none focus:border-[#D4AF37]"
                />
                <input
                  data-testid={`row-email-${i}`}
                  value={r.email}
                  onChange={(e) => updateRow(i, "email", e.target.value)}
                  placeholder="email@example.com"
                  className="px-3 py-2 rounded-md bg-white/[0.05] border border-white/10 text-sm outline-none focus:border-[#D4AF37]"
                />
                <input
                  data-testid={`row-mobile-${i}`}
                  value={r.mobile}
                  onChange={(e) => updateRow(i, "mobile", e.target.value)}
                  placeholder="10-digit"
                  className="px-3 py-2 rounded-md bg-white/[0.05] border border-white/10 text-sm outline-none focus:border-[#D4AF37]"
                />
                <select
                  data-testid={`row-role-${i}`}
                  value={r.role}
                  onChange={(e) => updateRow(i, "role", e.target.value)}
                  className="px-2 py-2 rounded-md bg-white/[0.05] border border-white/10 text-xs outline-none focus:border-[#D4AF37]"
                >
                  {ROLES.map((rl) => (
                    <option key={rl} value={rl}>{rl.toUpperCase()}</option>
                  ))}
                </select>
                <button
                  data-testid={`row-remove-${i}`}
                  onClick={() => rmRow(i)}
                  className="text-white/40 hover:text-rose-300"
                  title="Remove this row"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={addRow}
              data-testid="add-row"
              className="mt-2 text-xs text-[#F0D788] hover:underline"
            >
              + Add another row
            </button>
          </div>
        )}

        {err && (
          <div className="mx-5 mb-3 rounded-md bg-rose-500/15 border border-rose-500/30 text-rose-100 text-xs px-3 py-2">
            {err}
          </div>
        )}

        <div className="p-5 border-t border-white/5 flex items-center gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-white/15 text-white/75 text-sm"
          >
            Cancel
          </button>
          <button
            data-testid="send-invites-submit"
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-70"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Sending…" : `Send ${rows.filter((r) => r.name && r.email).length} invitation(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}
